<script setup>
import { ref, onMounted, reactive, computed } from 'vue';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import { Country, State, City } from 'country-state-city';

const router = useRouter();
const isLoading = ref(true);
const processingId = ref(null);
const search = ref('');

// Form validation state
const valid = ref(true);
const dialog = ref(false);
const deleteDialog = ref(false);
const agentToDelete = ref(null);
const processing = ref(false);
const editedIndex = ref(-1);
const editedItem = reactive({
	id: null,
	country: '',
	state: '',
	city: '',
	portName: '',
	portAbbr: '',
	agentName: '',
	active: true
});
const defaultItem = {
	id: null,
	country: '',
	state: '',
	city: '',
	portName: '',
	portAbbr: '',
	agentName: '',
	active: true
};

// Set up country data from the country-state-city library
let USIndex;
const CountryList = Country.getAllCountries().map((item, index) => {
	if (item.isoCode === "US") USIndex = index;
	return {
		name: item.name,
		isoCode: item.isoCode,
	}
});

// Move US to the top of the list
if (USIndex !== undefined) {
	let tmp = CountryList[USIndex];
	CountryList.splice(USIndex, 1);
	CountryList.unshift(tmp);
}

// States data - will be populated based on selected country
const states = ref({});

// Sample data - in a real application, this would come from an API
const portAgents = ref([]);

// Computed values for filtering states based on selected country
const availableStates = computed(() => {
	if (!editedItem.country) return [];

	// Find the country code based on name
	const countryObject = CountryList.find(c => c.name === editedItem.country);
	if (!countryObject) return [];

	// Get states for the selected country
	const countryStates = State.getStatesOfCountry(countryObject.isoCode);
	return countryStates;
});

const availableCities = computed(() => {
	if (!editedItem.state) return [];

	// Find the country code based on name
	const statesObject = availableStates.value.find(c => c.name === editedItem.state);
	
	if (!statesObject) return [];

	// Get states for the selected country
	const stateCities = City.getCitiesOfState(statesObject.countryCode, statesObject.isoCode);
	return stateCities;
});

const filteredAgents = computed(() => {
	if (!search.value) {
		return portAgents.value;
	}

	const searchTerm = search.value.toLowerCase();
	return portAgents.value.filter(item =>
		item.portAbbr.toLowerCase().includes(searchTerm) ||
		item.name.toLowerCase().includes(searchTerm) ||
		item.country.toLowerCase().includes(searchTerm) ||
		item.state.toLowerCase().includes(searchTerm) ||
		item.city.toLowerCase().includes(searchTerm) ||
		item.portName.toLowerCase().includes(searchTerm)
	);
});

// Computed values
const formTitle = computed(() => {
	return editedIndex.value === -1 ? 'New Port Agent' : 'Edit Port Agent';
});

// Methods
const fetchPortAgents = async () => {
	isLoading.value = true;
	try {
		// In a real app, fetch data from API
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/port-agents`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		});

		if (!response.ok) {
			throw Error("Something went wrong!");
		}

		let res = await response.json();

		res = Array.from(res['portAgents']).map((item) => {
			return {
				...item,
				portAbbr: item.port_abbr,
				portName: item.port_name,
				agentName: item.agent_name,
				active: item.is_active,
			}
		});

		// console.log(res);

		portAgents.value = res;
	} catch (error) {
		showSnackbar('Failed to load port agents', 'error');
		console.error('Error fetching port agents:', error);
	} finally {
		isLoading.value = false;
	}
};

const openAddDialog = () => {
	editedIndex.value = -1;
	Object.assign(editedItem, defaultItem);
	dialog.value = true;
};

const editAgent = (item) => {
	editedIndex.value = portAgents.value.findIndex(a => a.id === item.id);
	Object.assign(editedItem, JSON.parse(JSON.stringify(item)));
	dialog.value = true;
};

const closeDialog = () => {
	dialog.value = false;
	setTimeout(() => {
		Object.assign(editedItem, defaultItem);
		editedIndex.value = -1;
	}, 300);
};

const saveAgent = async () => {
	if (!editedItem.country) {
		showSnackbar('Please fill in the country which this port belongs', 'error');
		return;
	}


	const isUS = editedItem.country === "United States";

	if(isUS && (!editedItem.state || !editedItem.city)){
		showSnackbar('Please fill in the correct state/district and city this port belongs to.', 'error');
		return;
	}

	if(!editedItem.portName || !editedItem.portAbbr || !editedItem.agentName) {
		showSnackbar('Please fill all required items.', 'error');
		return;
	}
	

	processing.value = true;

	try {
		// Check if abbreviation already exists when adding new agent
		if (editedIndex.value === -1) {

			// In a real app, make API call to create a new port agent
			const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/port-agents`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
				},
				body: JSON.stringify({
					country: editedItem.country,
					state: editedItem.state,
					city: editedItem.city,
					portName: editedItem.portName,
					portAbbr: editedItem.portAbbr,
					agentName: editedItem.agentName,
					active: editedItem.active
				})
			});

			if(!response.ok) {
				showSnackbar(`Sorry could not add port agent.`, 'error');
				processing.value = false;
				return;
			}

			const j = await response.json();

			

			// Simulate API delay
			// await new Promise(resolve => setTimeout(resolve, 500));

			const newAgent = {
				...j['portAgent'],
				country: editedItem.country,
				state: editedItem.state,
				city: editedItem.city,
				portName: editedItem.portName,
				portAbbr: editedItem.portAbbr,
				agentName: editedItem.agentName,
				active: editedItem.active
			};

			portAgents.value.push(newAgent);
			showSnackbar('Port agent added successfully', 'success');
		} else {
			
			const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/port-agents/${editedItem.id}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
				},
				body: JSON.stringify({
					country: editedItem.country,
					state: editedItem.state,
					city: editedItem.city,
					portName: editedItem.portName,
					portAbbr: editedItem.portAbbr,
					agentName: editedItem.agentName,
					active: editedItem.active
				})
			});

			if(!response.ok) {
				showSnackbar("There was an error while updating the record", "error");
				return;
			}

			const j = await response.json();
			const _updatedItem = j['portAgent'];

			// Simulate API delay
			// await new Promise(resolve => setTimeout(resolve, 500));

			// Update existing agent (except abbreviation which cannot be edited)

			const agent = portAgents.value.find( (item) => item.id === editedItem.id);
			// const agent = portAgents.value[editedIndex.value];
			agent.country = _updatedItem.country;
			agent.portAbbr = _updatedItem.port_abbr;
			agent.state = _updatedItem.state;
			agent.city = _updatedItem.city;
			agent.portName = _updatedItem.port_name;
			agent.agentName = _updatedItem.agent_name;
			agent.active = _updatedItem.is_active;

			showSnackbar('Port agent updated successfully', 'success');
		}

		closeDialog();
	} catch (error) {
		showSnackbar('Operation failed', 'error');
		console.error('Error saving port agent:', error);
	} finally {
		processing.value = false;
	}
};

const confirmDelete = (item) => {
	agentToDelete.value = item;
	deleteDialog.value = true;
};

const deleteAgent = async () => {
	processing.value = true;
	try {
		// In a real app, make API call to delete port agent
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/port-agents/${agentToDelete.value.id}`, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			},
		});

		if(response.ok) {
			const index = portAgents.value.findIndex(a => a.id === agentToDelete.value.id);
			portAgents.value.splice(index, 1);
	
			showSnackbar('Port agent deleted successfully', 'success');
			deleteDialog.value = false;
		} else {
			throw Error("Failed to delete port agent due to unknown reasons");
		}
	} catch (error) {
		showSnackbar('Failed to delete port agent', 'error');
		console.error('Error deleting port agent:', error);
	} finally {
		processing.value = false;
		agentToDelete.value = null;
	}
};

const toggleAgentStatus = async (item) => {
	processingId.value = item.id;
	try {
		// In a real app, make API call to toggle port agent status
		const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/port-agents/${item.id}`, {
			method: "PUT",
			body: JSON.stringify({
				isActive: !item.active
			}),
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			},
		});

		item.active = !item.active;
		showSnackbar(`Port agent ${item.active ? 'enabled' : 'disabled'} successfully`, 'success');
	} catch (error) {
		// Revert the toggle if it fails
		item.active = !item.active;
		showSnackbar('Failed to update port agent status', 'error');
		console.error('Error toggling port agent status:', error);
	} finally {
		processingId.value = null;
	}
};

const showSnackbar = (text, color = 'success') => {
	// Using vue3-toastify
	if (color === 'error') {
		toast?.error(text);
	} else {
		toast?.success(text);
	}
};

// Initialize on component mount
onMounted(async () => {
	// Check authentication before loading the page
	const token = localStorage.getItem('auth_token');
	const expiresAt = localStorage.getItem('expiresAt');

	// If no token or token is expired, redirect to login
	if (!token || !expiresAt || new Date(expiresAt).getTime() <= new Date().getTime()) {
		toast?.error("Authentication required");
		router.push('/login');
		return;
	}

	await fetchPortAgents();
});
</script>

<script>
const INPUT_CLASS = "rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 disabled:bg-gray-200 disabled:text-gray-300";

const MIMES = "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/rtf, text/plain, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv, application/xml".split(", ");

const $ = (selector) => document.querySelector(selector);

export default {
	setup() {
		return {
			INPUT_CLASS,
			MIMES,
		}
	},
}
</script>

<template>
	<!-- Loading Spinner while loading data -->
	<div v-if="isLoading" class="w-full h-screen flex items-center justify-center bg-page">
		<div class="flex flex-col items-center">
			<div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
			<p class="mt-4 text-primary font-monster font-medium">Loading...</p>
		</div>
	</div>

	<!-- Main content - only shown when data is loaded -->
	<div v-else class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll">
		<div class="p-2 bg-page">
			<h4 class="text-primary font-bold font-monster -ml-2">
				Port Agents Management
			</h4>
		</div>

		<div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6">
			<!-- Header with search and add button -->
			<div class="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
				<!-- Search field -->
				<div class="w-full md:w-1/3 relative">
					<input v-model="search" type="text" placeholder="Search port agents..."
						class="rounded-[10px] pl-10 pr-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 w-full" />
					<svg class="absolute left-3 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24"
						fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
							stroke="#020754" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M21 21L16.65 16.65" stroke="#020754" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round" />
					</svg>
				</div>

				<!-- Add port agent button -->
				<button @click="openAddDialog"
					class="font-monster flex justify-center gap-2 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white py-3 px-4">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12 5V19" stroke="white" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round" />
						<path d="M5 12H19" stroke="white" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round" />
					</svg>
					Add Port Agent
				</button>
			</div>

			<!-- Port agents table -->
			<div class="overflow-x-auto">
				<table class="min-w-full divide-y divide-gray-200 table-fixed">
					<thead>
						<tr class="font-monster font-bold text-secondary text-left">
							<th class="py-3 px-4">Abbreviation</th>
							<th class="py-3 px-4">Port Name</th>
							<th class="py-3 px-4">Country</th>
							<th class="py-3 px-4">State/District</th>
							<th class="py-3 px-4">City</th>
							<th class="py-3 px-4">Agent Name </th>
							<th class="py-3 px-4">Status</th>
							<th class="py-3 px-4">Actions</th>
						</tr>
					</thead>
					<tbody class="bg-white divide-y divide-gray-100">
						<tr v-for="agent in filteredAgents" :key="agent.id" class="hover:bg-gray-50">
							<td class="py-3 px-4 font-monster font-medium">{{ agent.portAbbr }}</td>
							<td class="py-3 px-4 font-monster">{{ agent.portName }}</td>
							<td class="py-3 px-4 font-monster">{{ agent.country }}</td>
							<td class="py-3 px-4 font-monster">{{ agent.state }}</td>
							<td class="py-3 px-4 font-monster">{{ agent.city }}</td>
							<td class="py-3 px-4 font-monster">{{ agent.agentName }}</td>
							<td class="py-3 px-4">
								<div class="w-12 h-6 flex items-center relative cursor-pointer"
									@click="toggleAgentStatus(agent)">
									<div class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
										:class="agent.active ? 'bg-secondary' : 'bg-gray-300'">
										<div class="w-5 h-5 bg-white rounded-full shadow absolute top-0.5 left-0.5 transition-transform duration-200 ease-in-out"
											:class="agent.active ? 'transform translate-x-6' : ''"
											:style="processingId === agent.id ? 'opacity: 0.5;' : ''"></div>
									</div>
								</div>
							</td>
							<td class="py-3 px-4">
								<div class="flex items-center gap-2">
									<button @click="editAgent(agent)"
										class="text-secondary hover:text-primary transition-colors duration-200"
										:disabled="processingId === agent.id">
										<svg width="20" height="20" viewBox="0 0 24 24" fill="none"
											xmlns="http://www.w3.org/2000/svg">
											<path
												d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
												stroke="currentColor" stroke-width="2" stroke-linecap="round"
												stroke-linejoin="round" />
											<path
												d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
												stroke="currentColor" stroke-width="2" stroke-linecap="round"
												stroke-linejoin="round" />
										</svg>
									</button>
									<button @click="confirmDelete(agent)"
										class="text-primary hover:text-red-700 transition-colors duration-200"
										:disabled="processingId === agent.id">
										<svg width="20" height="20" viewBox="0 0 24 24" fill="none"
											xmlns="http://www.w3.org/2000/svg">
											<path d="M3 6H5H21" stroke="currentColor" stroke-width="2"
												stroke-linecap="round" stroke-linejoin="round" />
											<path
												d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
												stroke="currentColor" stroke-width="2" stroke-linecap="round"
												stroke-linejoin="round" />
										</svg>
									</button>
								</div>
							</td>
						</tr>
						<tr v-if="filteredAgents.length === 0">
							<td colspan="8" class="py-6 text-center text-gray-500 font-monster">
								No port agents found
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!-- Add/Edit Dialog -->
		<div v-if="dialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div class="bg-white rounded-lg max-w-2xl w-full p-6">
				<h2 class="text-xl font-monster font-bold text-secondary mb-4">{{ formTitle }}</h2>

				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<!-- Port Agent Abbreviation -->
					<div>
						<label class="font-monster text-secondary block mb-1">
							Port Abbreviation
						</label>
						<input v-model="editedItem.portAbbr"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							 placeholder="E.g. USNYC" />
					</div>
					<!-- Country - Changed to datalist input -->
					<div>
						<label class="font-monster text-secondary block mb-1">Country</label>
						<input list="country-list" v-model="editedItem.country"
							:class="twMerge(INPUT_CLASS, 'w-full', clsx({ 'error': false }))"
							placeholder="Select or type a country" />
						<datalist id="country-list">
							<option v-for="country in CountryList" :key="country.isoCode" :value="country.name">
								{{ country.isoCode }}
							</option>
						</datalist>
					</div>

					<div>
						<label
							:class="twMerge('font-monster text-secondary block mb-1', clsx({ 'not-required': editedItem.country !== 'United States' }))">State/District</label>
						<input list="state-list" v-model="editedItem.state"
							:class="twMerge(INPUT_CLASS, 'w-full', clsx({ 'error': false }))"
							placeholder="Select or type a state/district" />
						<datalist id="state-list">
							<option v-for="state in availableStates" :key="state.isoCode" :value="state.name">
								{{ state.name }}
							</option>
						</datalist>
					</div>

					<div>
						<label
							:class="twMerge('font-monster text-secondary block mb-1', clsx({ 'not-required': editedItem.country !== 'United States' }))">City</label>
						<input list="city-list" v-model="editedItem.city"
							:class="twMerge(INPUT_CLASS, 'w-full', clsx({ 'error': false }))"
							placeholder="Select or type a city" />
						<datalist id="city-list">
							<option v-for="city in availableCities" :key="city.name" :value="city.name">
								{{ city.name }}
							</option>
						</datalist>
					</div>


					<!-- Port Name - Free form input -->
					<div>
						<label class="font-monster text-secondary block mb-1">Port Name</label>
						<input v-model="editedItem.portName"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							placeholder="Enter port name" />
					</div>



					<!-- Port Agent Name -->
					<div>
						<label class="font-monster text-secondary block mb-1">
							Port Agent Name
						</label>
						<input v-model="editedItem.agentName"
							class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
							placeholder="Enter port agent name" />
					</div>
				</div>

				<!-- Status toggle -->
				<div class="mt-4 mb-6 flex items-center">
					<div class="w-12 h-6 flex items-center relative cursor-pointer mr-2"
						@click="editedItem.active = !editedItem.active">
						<div class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
							:class="editedItem.active ? 'bg-secondary' : 'bg-gray-300'">
							<div class="w-5 h-5 bg-white rounded-full shadow absolute top-0.5 left-0.5 transition-transform duration-200 ease-in-out"
								:class="editedItem.active ? 'transform translate-x-6' : ''"></div>
						</div>
					</div>
					<label class="font-monster text-secondary not-required">Active</label>
				</div>

				<div class="flex justify-end gap-3">
					<button @click="closeDialog"
						class="font-monster py-2 px-4 rounded-[0.625rem] border border-stroke text-secondary hover:bg-gray-50 transition-colors">
						Cancel
					</button>
					<button @click="saveAgent" :disabled="processing"
						class="font-monster py-2 px-4 rounded-[0.625rem] bg-primary text-white hover:bg-primary/80 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2">
						<svg v-if="processing" class="animate-spin h-4 w-4 text-white"
							xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
							</circle>
							<path class="opacity-75" fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
							</path>
						</svg>
						Save
					</button>
				</div>
			</div>
		</div>

		<!-- Delete Confirmation Dialog -->
		<div v-if="deleteDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div class="bg-white rounded-lg max-w-md w-full p-6">
				<h2 class="text-xl font-monster font-bold text-secondary mb-2">Delete Port Agent</h2>
				<p class="mb-6 text-gray-600">
					Are you sure you want to delete this port agent? This action cannot be undone.
				</p>
				<div v-if="agentToDelete" class="mb-6 p-3 bg-gray-50 rounded-lg">
					<p class="font-monster"><span class="font-bold text-secondary">Abbreviation:</span> {{ agentToDelete.portAbbr }}</p>
					<p class="font-monster"><span class="font-bold text-secondary">Name:</span> {{ agentToDelete.agentName }}</p>
					<p class="font-monster"><span class="font-bold text-secondary">Port:</span> {{ agentToDelete.portName }}</p>
				</div>

				<div class="flex justify-end gap-3">
					<button @click="deleteDialog = false"
						class="font-monster py-2 px-4 rounded-[0.625rem] border border-stroke text-secondary hover:bg-gray-50 transition-colors">
						Cancel
					</button>
					<button @click="deleteAgent" :disabled="processing"
						class="font-monster py-2 px-4 rounded-[0.625rem] bg-primary text-white hover:bg-primary/80 transition-colors flex items-center gap-2">
						<svg v-if="processing" class="animate-spin h-4 w-4 text-white"
							xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
							</circle>
							<path class="opacity-75" fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
							</path>
						</svg>
						Delete
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="css" scoped>
/* Custom style for buttons when disabled/processing */
button:disabled {
	opacity: 0.7;
	cursor: not-allowed;
}

input::-webkit-calendar-picker-indicator {
	opacity: 0 !important;
	position: absolute;
	margin-top: -6px;
	right: 12px;
}

label:not(.not-required):after {
	content: " *"
}
</style>
